import { create } from 'zustand';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallState {
  deferredPrompt: BeforeInstallPromptEvent | null;
  isInstalled: boolean;
  showBanner: boolean;
  isDismissed: boolean;

  setDeferredPrompt: (prompt: BeforeInstallPromptEvent | null) => void;
  setIsInstalled: (installed: boolean) => void;
  dismissBanner: () => void;
  triggerInstall: () => Promise<boolean>;
}

// Cles de dismiss separees pour touriste et organisateur
const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_KEY_ORGA = 'pwa-orga-install-dismissed';
const DISMISS_DURATION_DAYS = 7;

// === Utilitaires pour le parametre ?install=true ===

/**
 * Detecte si le parametre ?install=true est present dans l'URL
 */
export const checkInstallParam = (): boolean => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('install') === 'true';
};

/**
 * Retire le parametre ?install=true de l'URL sans recharger la page
 */
export const removeInstallParam = (): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('install');
  window.history.replaceState({}, '', url.toString());
};

// Check if banner was dismissed recently
const wasDismissedRecently = (): boolean => {
  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  if (!dismissedAt) return false;

  const dismissedDate = new Date(parseInt(dismissedAt, 10));
  const daysSinceDismiss = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceDismiss < DISMISS_DURATION_DAYS;
};

export const usePWAInstallStore = create<PWAInstallState>((set, get) => ({
  deferredPrompt: null,
  isInstalled: false,
  showBanner: false,
  isDismissed: wasDismissedRecently(),

  setDeferredPrompt: (prompt) => {
    const { isDismissed, isInstalled } = get();
    set({
      deferredPrompt: prompt,
      showBanner: prompt !== null && !isDismissed && !isInstalled
    });
  },

  setIsInstalled: (installed) => {
    set({
      isInstalled: installed,
      showBanner: false
    });
  },

  dismissBanner: () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    set({
      showBanner: false,
      isDismissed: true
    });
  },

  triggerInstall: async () => {
    const { deferredPrompt } = get();
    if (!deferredPrompt) return false;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        set({
          deferredPrompt: null,
          showBanner: false,
          isInstalled: true
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('PWA install error:', error);
      return false;
    }
  }
}));

// Hook pour initialiser les listeners (à appeler une fois dans App)
export const initPWAInstallListeners = () => {
  const store = usePWAInstallStore.getState();

  // Check if already installed
  if (window.matchMedia('(display-mode: standalone)').matches) {
    store.setIsInstalled(true);
    return;
  }

  // Listen for install prompt
  const handleBeforeInstallPrompt = (e: Event) => {
    e.preventDefault();
    store.setDeferredPrompt(e as BeforeInstallPromptEvent);
  };

  // Listen for successful install
  const handleAppInstalled = () => {
    store.setIsInstalled(true);
  };

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  window.addEventListener('appinstalled', handleAppInstalled);

  return () => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.removeEventListener('appinstalled', handleAppInstalled);
  };
};

// === PWA Organisateur Store ===
// Store separe pour la PWA Organisateur avec sa propre cle de dismiss

interface PWAOrgaInstallState {
  deferredPrompt: BeforeInstallPromptEvent | null;
  isInstalled: boolean;
  showPrompt: boolean;
  isDismissed: boolean;
  installParamDetected: boolean;

  setDeferredPrompt: (prompt: BeforeInstallPromptEvent | null) => void;
  setIsInstalled: (installed: boolean) => void;
  dismissPrompt: () => void;
  triggerInstall: () => Promise<boolean>;
  checkAndSetInstallParam: () => void;
}

// Check si le banner orga a ete dismiss recemment
const wasOrgaDismissedRecently = (): boolean => {
  const dismissedAt = localStorage.getItem(DISMISS_KEY_ORGA);
  if (!dismissedAt) return false;

  const dismissedDate = new Date(parseInt(dismissedAt, 10));
  const daysSinceDismiss = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceDismiss < DISMISS_DURATION_DAYS;
};

export const usePWAOrgaInstallStore = create<PWAOrgaInstallState>((set, get) => ({
  deferredPrompt: null,
  isInstalled: false,
  showPrompt: false,
  isDismissed: wasOrgaDismissedRecently(),
  installParamDetected: false,

  setDeferredPrompt: (prompt) => {
    const { isDismissed, isInstalled, installParamDetected } = get();
    set({
      deferredPrompt: prompt,
      // Afficher si: prompt disponible ET (param install OU pas dismiss) ET pas installe
      showPrompt: prompt !== null && (installParamDetected || !isDismissed) && !isInstalled
    });
  },

  setIsInstalled: (installed) => {
    set({
      isInstalled: installed,
      showPrompt: false
    });
  },

  dismissPrompt: () => {
    localStorage.setItem(DISMISS_KEY_ORGA, Date.now().toString());
    // Retirer le parametre ?install=true si present
    removeInstallParam();
    set({
      showPrompt: false,
      isDismissed: true,
      installParamDetected: false
    });
  },

  triggerInstall: async () => {
    const { deferredPrompt } = get();
    if (!deferredPrompt) return false;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      // Retirer le parametre ?install=true apres action
      removeInstallParam();

      if (outcome === 'accepted') {
        set({
          deferredPrompt: null,
          showPrompt: false,
          isInstalled: true,
          installParamDetected: false
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('PWA Orga install error:', error);
      return false;
    }
  },

  checkAndSetInstallParam: () => {
    const hasParam = checkInstallParam();
    if (hasParam) {
      set({ installParamDetected: true });
      // Re-evaluer showPrompt
      const { deferredPrompt, isInstalled } = get();
      if (deferredPrompt && !isInstalled) {
        set({ showPrompt: true });
      }
    }
  }
}));

// Hook pour initialiser les listeners PWA Organisateur
export const initPWAOrgaInstallListeners = () => {
  const store = usePWAOrgaInstallStore.getState();

  // Verifier le parametre ?install=true
  store.checkAndSetInstallParam();

  // Check if already installed (PWA mode)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    store.setIsInstalled(true);
    return;
  }

  // Listen for install prompt
  const handleBeforeInstallPrompt = (e: Event) => {
    e.preventDefault();
    store.setDeferredPrompt(e as BeforeInstallPromptEvent);
    // Re-verifier le parametre install apres avoir le prompt
    store.checkAndSetInstallParam();
  };

  // Listen for successful install
  const handleAppInstalled = () => {
    store.setIsInstalled(true);
  };

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  window.addEventListener('appinstalled', handleAppInstalled);

  return () => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.removeEventListener('appinstalled', handleAppInstalled);
  };
};
